import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import ProductList from './components/ProductList';
import useProductLookup from './hooks/useProductLookup';

function ProductLookup() {
  const [keysInput, setKeysInput] = useState('');
  const { results, isLoading, error, lookupKeys, clearResults } = useProductLookup();

  const handleSearch = async () => {
    if (!keysInput.trim()) return;
    try {
      await lookupKeys(keysInput);
    } catch {
      // Error is already handled in the hook
    }
  };

  const handleClear = () => {
    setKeysInput('');
    clearResults();
  };

  const handleDownload = () => {
    if (!results) return;
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-lookup-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Product Lookup
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          label="Digital Asset Collection Keys"
          placeholder="Enter keys separated by commas or newlines (e.g., tw7339, 11710, tw7016)"
          multiline
          rows={3}
          fullWidth
          value={keysInput}
          onChange={(e) => setKeysInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          helperText="Keys can be entered with or without the 'tw' prefix"
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={isLoading || (!keysInput && !results)}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={isLoading || !keysInput.trim()}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results && (
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Found {results.total_found} of {results.total_requested} products
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download JSON
            </Button>
          </Box>
          <ProductList results={results.results} />
        </>
      )}
    </Box>
  );
}

export default ProductLookup;
